<template>
  <div>
    <b-card class="shadow">
      <div class="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h4 class="mb-0">Configurações do RMA</h4>
          <p class="text-muted mb-0">
            Defina quais tipos vinculados aos itens do RMA serão tratados como
            fixos.
          </p>
        </div>

        <button
          class="btn btn-success btn-submit m-0"
          data-style="zoom-in"
          @click="salvarConfiguracoesRma"
          :disabled="!itemSelecionado || loadingConfiguracao"
        >
          <i class="mdi mdi-content-save" />
          Salvar
        </button>
      </div>

      <div class="alert alert-warning">
        <strong>Atenção!</strong>
        Quando um tipo estiver marcado como <strong>fixo</strong>, ele não
        poderá ser removido nem desativado na tela de parametrização comum do
        RMA.
      </div>

      <div class="row mb-3">
        <div class="col-md-4">
          <label>Relatório</label>
          <b-form-select
            v-model="filtros.relatorio"
            :options="relatorioOptions"
            @change="getItens"
          />
        </div>

        <div class="col-md-4">
          <label>Fonte</label>
          <b-form-select
            v-model="filtros.fonte"
            :options="fonteOptions"
            @change="getItens"
          />
        </div>

        <div class="col-md-4">
          <label>Pesquisar item</label>
          <div class="input-group">
            <input
              class="form-control"
              v-model="filtros.search"
              placeholder="Ex: C.1, M.1, atendimento..."
              @keyup.enter="getItens"
            />

            <div class="input-group-append">
              <button class="btn btn-primary" @click="getItens">
                <i class="fas fa-search" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <!-- Lista de itens -->
        <div class="col-md-5">
          <div class="card rma-itens-card">
            <div class="card-header bg-white">
              <strong>Itens do RMA</strong>
            </div>

            <div class="card-body p-0 rma-itens-scroll">
              <load v-if="loadingItens" />

              <div v-else-if="itens.length === 0" class="text-center p-4">
                <p class="font-weight-bold mb-1">Nenhum item encontrado</p>
                <small class="text-muted"
                  >Altere os filtros e tente novamente.</small
                >
              </div>

              <div v-else>
                <div v-for="grupo in itensAgrupados" :key="grupo.bloco">
                  <div class="px-3 py-2 bg-light border-top border-bottom">
                    <small class="font-weight-bold text-muted">
                      {{ grupo.bloco || "Sem bloco" }}
                    </small>
                  </div>

                  <b-list-group flush>
                    <b-list-group-item
                      v-for="item in grupo.itens"
                      :key="item.id"
                      button
                      :active="
                        itemSelecionado && itemSelecionado.id === item.id
                      "
                      @click="selecionarItem(item)"
                    >
                      <div
                        class="d-flex justify-content-between align-items-start"
                      >
                        <div>
                          <strong>{{ item.codigo }}</strong>
                          <div class="small">{{ item.titulo }}</div>
                          <div class="small text-muted mt-1">
                            Fonte: {{ labelFonte(item.fonte_principal) }}
                          </div>
                        </div>

                        <b-badge
                          :variant="
                            item.permite_configuracao ? 'success' : 'secondary'
                          "
                        >
                          {{
                            item.permite_configuracao
                              ? "Configurável"
                              : "Não configurável"
                          }}
                        </b-badge>
                      </div>
                    </b-list-group-item>
                  </b-list-group>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Mapeamentos -->
        <div class="col-md-7">
          <div class="card">
            <div class="card-header bg-white">
              <strong v-if="itemSelecionado">
                {{ itemSelecionado.codigo }} - {{ itemSelecionado.titulo }}
              </strong>

              <strong v-else> Mapeamentos do item </strong>
            </div>

            <div class="card-body">
              <div v-if="!itemSelecionado" class="text-center py-5">
                <h5 class="font-weight-bold">Selecione um item do RMA</h5>
                <p class="text-muted mb-0">
                  Escolha um item na lista ao lado para alterar quais tipos são
                  fixos.
                </p>
              </div>

              <div v-else>
                <load v-if="loadingConfiguracao" />

                <div v-else>
                  <div class="border rounded p-3 mb-3 bg-light">
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        <strong>Permitir parametrização deste item</strong>
                        <div class="small text-muted">
                          Quando ativado, usuários poderão adicionar, remover ou desabilitar tipos opcionais
                          na tela comum de parametrização do RMA.
                        </div>
                      </div>

                      <b-form-checkbox
                        switch
                        v-model="itemSelecionado.permite_configuracao"
                        @change="itemConfiguracaoAlterada = true"
                      >
                        {{ itemSelecionado.permite_configuracao ? "Configurável" : "Não configurável" }}
                      </b-form-checkbox>
                    </div>
                  </div>
                  <div class="mb-3">
                    <small class="text-muted">
                      Marque como <strong>Fixo</strong> os tipos que não poderão
                      ser removidos pelos usuários na parametrização do RMA.
                    </small>
                  </div>

                  <b-table
                    :fields="fields"
                    :items="mapeamentos"
                    responsive
                    striped
                    hover
                    small
                    outlined
                    show-empty
                  >
                    <template v-slot:cell(fonte_descricao)="data">
                      <strong>{{ data.item.fonte_descricao }}</strong>
                      <div class="small text-muted">
                        {{ data.item.fonte_tipo_label }}
                      </div>
                    </template>

                    <template v-slot:cell(origem)="data">
                      <b-badge :variant="variantOrigem(data.item.origem)">
                        {{ data.item.origem }}
                      </b-badge>
                    </template>

                    <template v-slot:cell(habilitado)="data">
                      <b-badge
                        :variant="
                          data.item.habilitado ? 'success' : 'secondary'
                        "
                      >
                        {{ data.item.habilitado ? "Sim" : "Não" }}
                      </b-badge>
                    </template>

                    <template v-slot:cell(obrigatorio)="data">
                      <b-form-checkbox
                        switch
                        v-model="data.item.obrigatorio"
                        @change="marcarAlterado(data.item)"
                      >
                        {{ data.item.obrigatorio ? "Fixo" : "Editável" }}
                      </b-form-checkbox>
                    </template>

                    <template v-slot:empty>
                      <div class="text-center p-4">
                        <p class="font-weight-bold mb-1">
                          Nenhum tipo vinculado
                        </p>
                        <small class="text-muted">
                          Este item ainda não possui mapeamentos cadastrados.
                        </small>
                      </div>
                    </template>
                  </b-table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
export default {
  name: "RmaSettings",

  data() {
    return {
      relatorios: [],
      fontes: [],

      filtros: {
        relatorio: "cras",
        fonte: null,
        search: "",
      },

      itens: [],
      itemSelecionado: null,
      mapeamentos: [],

      loadingItens: false,
      loadingConfiguracao: false,

      itemConfiguracaoAlterada: false,

      fields: [
        {
          key: "fonte_descricao",
          label: "Tipo",
        },
        {
          key: "origem",
          label: "Origem",
        },
        {
          key: "habilitado",
          label: "Habilitado",
        },
        {
          key: "obrigatorio",
          label: "Fixo",
          thStyle: { width: "150px" },
        },
      ],
    };
  },

  computed: {
    relatorioOptions() {
      return this.relatorios.map((item) => ({
        value: item.codigo,
        text: item.nome,
      }));
    },

    fonteOptions() {
      const options = [
        {
          value: null,
          text: "Todas",
        },
      ];

      this.fontes.forEach((fonte) => {
        options.push({
          value: fonte.codigo,
          text: fonte.nome,
        });
      });

      return options;
    },

    itensAgrupados() {
      const grupos = {};

      this.itens.forEach((item) => {
        const bloco = item.bloco || "Sem bloco";

        if (!grupos[bloco]) {
          grupos[bloco] = [];
        }

        grupos[bloco].push(item);
      });

      return Object.keys(grupos).map((bloco) => ({
        bloco,
        itens: grupos[bloco],
      }));
    },
  },

  mounted() {
    this.getRelatorios();
    this.getFontes();
    this.getItens();
  },

  methods: {
    selecionarItem(item) {
      this.itemSelecionado = {
        ...item,
        permite_configuracao: !!item.permite_configuracao,
      };

      this.itemConfiguracaoAlterada = false;
      this.getConfiguracaoItem();
    },
    getRelatorios() {
      axios.get("api/dashboard/admin/rma/relatorios").then((response) => {
        this.relatorios = response.data.data;
      });
    },

    getFontes() {
      axios.get("api/dashboard/admin/rma/fontes").then((response) => {
        this.fontes = response.data.data;
      });
    },

    getItens() {
      this.loadingItens = true;
      this.itemSelecionado = null;
      this.mapeamentos = [];

      axios
        .get(
          `api/dashboard/admin/rma/relatorios/${this.filtros.relatorio}/itens`,
          {
            params: {
              fonte: this.filtros.fonte,
              search: this.filtros.search,
            },
          }
        )
        .then((response) => {
          this.itens = response.data.data;

          if (this.itens.length > 0) {
            this.selecionarItem(this.itens[0]);
          }
        })
        .finally(() => {
          this.loadingItens = false;
        });
    },

    selecionarItem(item) {
      this.itemSelecionado = item;
      this.getConfiguracaoItem();
    },

    getConfiguracaoItem() {
      if (!this.itemSelecionado) {
        return;
      }

      this.loadingConfiguracao = true;

      axios
        .get(
          `api/dashboard/admin/rma/itens/${this.itemSelecionado.id}/configuracao`
        )
        .then((response) => {
          const data = response.data.data;

          this.itemSelecionado = {
            ...data.item,
            permite_configuracao: !!data.item.permite_configuracao,
          };

          this.mapeamentos = data.mapeamentos.map((item) => ({
            ...item,
            obrigatorio: !!item.obrigatorio,
            habilitado: !!item.habilitado,
            alterado: false,
          }));

          this.itemConfiguracaoAlterada = false;
        })
        .finally(() => {
          this.loadingConfiguracao = false;
        });
    },

    salvarConfiguracoesRma() {
      if (!this.itemSelecionado) {
        return;
      }

      const ladda = Ladda.create(document.querySelector(".btn-submit"));
      ladda.start();

      const promises = [];

      promises.push(
        axios.post(
          `api/dashboard/admin/rma/itens/${this.itemSelecionado.id}/configurabilidade`,
          {
            permite_configuracao: this.itemSelecionado.permite_configuracao,
          }
        )
      );

      promises.push(
        axios.post(
          `api/dashboard/admin/rma/itens/${this.itemSelecionado.id}/mapeamentos/obrigatoriedade`,
          {
            mapeamentos: this.mapeamentos.map((item) => ({
              id: item.id,
              obrigatorio: item.obrigatorio,
            })),
          }
        )
      );

      Promise.all(promises)
        .then(() => {
          this.$snack.success({
            button: false,
            text: "Configurações do RMA atualizadas com sucesso.",
          });

          this.getItens();
        })
        .finally(() => {
          ladda.stop();
        });
    },

    marcarAlterado(item) {
      item.alterado = true;

      if (item.obrigatorio) {
        item.habilitado = true;
      }
    },

    salvarObrigatoriedade() {
      if (!this.itemSelecionado) {
        return;
      }

      const ladda = Ladda.create(document.querySelector(".btn-submit"));
      ladda.start();

      axios
        .post(
          `api/dashboard/admin/rma/itens/${this.itemSelecionado.id}/mapeamentos/obrigatoriedade`,
          {
            mapeamentos: this.mapeamentos.map((item) => ({
              id: item.id,
              obrigatorio: item.obrigatorio,
            })),
          }
        )
        .then((response) => {
          this.$snack.success({
            button: false,
            text: response.data.data.message,
          });

          this.getConfiguracaoItem();
        })
        .finally(() => {
          ladda.stop();
        });
    },

    labelFonte(codigo) {
      const fonte = this.fontes.find((item) => item.codigo === codigo);

      if (fonte) {
        return fonte.nome;
      }

      return codigo;
    },

    variantOrigem(origem) {
      if (origem === "federal") {
        return "primary";
      }

      if (origem === "sistema") {
        return "info";
      }

      return "secondary";
    },
  },
};
</script>

<style scoped>
.rma-itens-card {
  height: calc(100vh - 310px);
  min-height: 500px;
  display: flex;
  flex-direction: column;
}

.rma-itens-scroll {
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
}

.list-group-item.active {
  background-color: #5895ff;
  border-color: #5895ff;
}
</style>
